import { closeNumber } from "../geometry/geometry.js";
import { CubicInterpolator } from "../util/math.js";
//some common value reducers: average, minimum, maximum and add
export const ScalarFieldValueReducers = {
    avg: (previousValue, value, index, list) => previousValue === undefined ? value / list.length : previousValue + value / list.length,
    min: (previousValue, value, index, list) => previousValue === undefined ? value : Math.min(previousValue, value),
    max: (previousValue, value, index, list) => previousValue === undefined ? value : Math.max(previousValue, value),
    add: (previousValue, value, index, list) => previousValue === undefined ? value : previousValue + value
};
class BaseScalarField {
    key;
    utm;
    bounds;
    time;
    stepSize;
    title;
    unit;
    properties;
    //reference to a cache system
    //private cacheKey: CacheKey;
    // a set of references to other scalar fields that provided values for
    // this field. When this field is an original only its own key is in
    // the set
    //private sources: Set<string>;
    // an array containing for each threshold value a collection (array) of isolines for
    // that value. Each isoline can be made up by 1 or more LineStrings. Each
    // LineString is either a normal isoline or a boundary line (a helper line in order
    // to close isolines that extend outside the boundaries of the scalar field ).
    //private isolines: { value: number, lines: LineString[][] }[];
    iterator;
    //public  [Symbol.iterator]: Iterable<ScalarFieldPoint>;
    constructor(key, utm, bounds, time, stepSize, title = "ScalarField", unit = "?", properties) {
        // this.cacheKey = undefined;
        // this.sources = new Set();
        this.key = key;
        this.utm = utm;
        this.bounds = bounds;
        this.time = time;
        this.stepSize = stepSize;
        this.title = title;
        this.unit = unit;
        this.properties = properties;
        let sf = this;
        this.iterator = function* (topToBottom) {
            let bounds = sf.utmBounds();
            let state = {
                index: { x: 0, y: 0 },
                position: { x: 0, y: 0 },
                time: sf.time,
                value: 0,
            };
            if (topToBottom !== false) {
                for (state.position.y = bounds.y.max; state.position.y >= bounds.y.min; state.position.y -= sf.stepSize.y) {
                    for (state.position.x = bounds.x.min; state.position.x <= bounds.x.max; state.position.x += sf.stepSize.x) {
                        state.value = sf.value(state.position.x, state.position.y);
                        yield {
                            index: { x: state.index.x, y: state.index.y },
                            position: { x: state.position.x, y: state.position.y },
                            time: state.time,
                            value: state.value,
                        };
                        state.index.x++;
                    }
                    state.index.y++;
                    state.index.x = 0;
                }
            }
            else {
                for (state.position.y = bounds.y.min; state.position.y <= bounds.y.max; state.position.y += sf.stepSize.y) {
                    for (state.position.x = bounds.x.min; state.position.x <= bounds.x.max; state.position.x += sf.stepSize.x) {
                        state.value = sf.value(state.position.x, state.position.y);
                        yield {
                            index: { x: state.index.x, y: state.index.y },
                            position: { x: state.position.x, y: state.position.y },
                            time: state.time,
                            value: state.value,
                        };
                        state.index.x++;
                    }
                    state.index.y--;
                    state.index.x = 0;
                }
            }
        };
    }
    [Symbol.iterator]() {
        return this.iterator();
    }
    getKey() {
        return this.key;
    }
    // public setCacheKey(cacheKey: CacheKey): void {
    // 	this.cacheKey = cacheKey;
    // 	this.sources.clear();
    // 	this.sources.add(cacheKey.key);
    // }
    // public getCacheKey(): CacheKey {
    // 	return this.cacheKey;
    // }
    // public addSource(key: string): void {
    // 	this.sources.add(key);
    // }
    // public hasSource(key: string): boolean {
    // 	return this.sources.has(key);
    // }
    // public addSources(sources: Set<string>): void {
    // 	sources.forEach(key => this.sources.add(key));
    // }
    // public getSources(): Set<string> {
    // 	return this.sources;
    // }
    setStepSize(stepSize) {
        this.stepSize = stepSize;
    }
    getStepSize() {
        return this.stepSize;
    }
    timeRange() {
        return this.time;
    }
    projection() {
        return this.utm;
    }
    getTitle() {
        return this.title;
    }
    getUnit() {
        return this.unit;
    }
    getProperties() {
        return this.properties;
    }
    utmBounds() {
        return this.bounds;
    }
    // public getIsolines(): { value: number, lines: LineString[][] }[] {
    // 	return this.isolines;
    // }
    // public calculateIsoLines(thresholds: number[], sample?: Point): { value: number, lines: LineString[][] }[] {
    // 	if (sample) {
    // 		this.setStepSize(sample);
    // 	}
    // 	this.isolines = getIsolines(this.iterator(), this.iterationBounds(), thresholds);
    // 	return this.isolines;
    // }
    iterationBounds() {
        let utmBounds = this.utmBounds();
        let size = {
            x: Math.floor((utmBounds.x.max - utmBounds.x.min) / this.stepSize.x) + 1,
            y: Math.floor((utmBounds.y.max - utmBounds.y.min) / this.stepSize.y) + 1,
        };
        return {
            x: {
                min: utmBounds.x.min,
                max: utmBounds.x.min + (size.x - 1) * this.stepSize.x,
                step: this.stepSize.x,
                count: size.x,
            },
            y: {
                min: utmBounds.y.max - (size.y - 1) * this.stepSize.y,
                max: utmBounds.y.max,
                step: -this.stepSize.y,
                count: size.y,
            },
        };
    }
    getAsTexture() {
        const bounds = this.iterationBounds();
        return {
            width: bounds.x.count,
            height: bounds.y.count,
            values: Float32Array.from([...this.iterator(false)], (sfp) => sfp.value),
        };
    }
}
/**
 * A scalar field backed by a grid with a certain pitch in memory. The
 * values between the known grid values are interpolated using bi-cubic
 * interpolation. Therefore the iterator step size can be changed independently
 * from the native grid pitch.
 */
export class BufferedScalarField extends BaseScalarField {
    nativeIterationBounds;
    nativeStepSize;
    values;
    outsideBoundsValue;
    dimensions;
    valueRange;
    point;
    nativeValue;
    // public asWorkerArgument(): any {
    // 	return {
    // 		type: 'BufferedScalarField',
    // 		key: this.getKey(),
    // 		utmHemisphere: this.projection().hemisphere(),
    // 		utmZone: this.projection().zone(),
    // 		nativeIterationBounds: this.utmBounds(),
    // 		time: this.timeRange(),
    // 		nativeStepSize: this.nativeStepSize,
    // 		values: this.values,
    // 		outsideBoundsValue: this.outsideBoundsValue,
    // 		title: this.getTitle(),
    // 		unit: this.getUnit(),
    // 		properties: this.getProperties(),
    // 		valuesAlreadyPrepared: true
    // 	}
    // }
    constructor(key, utm, nativeIterationBounds, time, nativeStepSize, values, outsideBoundsValue, title, unit, properties, valuesAlreadyPrepared) {
        super(key, utm, nativeIterationBounds, time, { x: Math.abs(nativeStepSize.x), y: Math.abs(nativeStepSize.y) }, title, unit, properties);
        this.nativeIterationBounds = nativeIterationBounds;
        this.nativeStepSize = nativeStepSize;
        this.values = values;
        this.outsideBoundsValue = outsideBoundsValue;
        this.dimensions = {
            x: this.nativeIterationBounds.x.count,
            y: this.nativeIterationBounds.y.count,
        };
        this.valueRange = this.values.reduce((range, value) => {
            if (!range) {
                range = {
                    min: value,
                    max: value,
                };
            }
            else {
                range.min = Math.min(range.min, value);
                range.max = Math.max(range.max, value);
            }
            return range;
        }, undefined);
        if (valuesAlreadyPrepared !== true) {
            this.prepareValues(); //add a ring of interpolated values around the originals
        }
        //helper methods for conversion between image-like indices and the underlying data structure
        const iXorg = nativeStepSize.x > 0
            ? (ix) => ix + 1
            : (ix) => this.dimensions.x + 2 - 1 - (ix + 1);
        const iYorg = nativeStepSize.y < 0
            ? (iy) => iy + 1
            : (iy) => this.dimensions.y + 2 - 1 - (iy + 1);
        const iOrg = this.nativeIterationBounds.rowOrientation
            ? (iXorg, iYorg) => iXorg + iYorg * (this.dimensions.x + 2)
            : (iXorg, iYorg) => iYorg + iXorg * (this.dimensions.y + 2);
        this.nativeValue = (x, y) => this.values[iOrg(iXorg(x), iYorg(y))];
        //helper methods for conversion between Cartesian and image-like indices
        const inside = (cx, cy) => cx ===
            Math.min(Math.max(cx, this.nativeIterationBounds.x.min), this.nativeIterationBounds.x.max) &&
            cy ===
                Math.min(Math.max(cy, this.nativeIterationBounds.y.min), this.nativeIterationBounds.y.max);
        const iXf = (cx) => (cx - this.nativeIterationBounds.x.min) /
            this.nativeIterationBounds.x.step; // Converts x from Cartesian to image-like float index.
        const iYf = (cy) => (this.nativeIterationBounds.y.max - cy) /
            -this.nativeIterationBounds.y.step; // Converts y from Cartesian to image-like float index.
        this.point = (cx, cy) => {
            if (inside(cx, cy)) {
                let point = {
                    index: { x: 0, y: 0, xf: iXf(cx), yf: iYf(cy) },
                    t: { x: 0, y: 0 },
                    position: { x: 0, y: 0 },
                    value: 0,
                };
                point.index.x = Math.floor(point.index.xf);
                point.index.y = Math.floor(point.index.yf);
                point.t.x = point.index.xf - point.index.x; // t is the relative distance from top left corner to actual given point (cx,cy)
                point.t.y = point.index.yf - point.index.y;
                point.value = this.nativeValue(point.index.x, point.index.y);
                point.position.x =
                    this.nativeIterationBounds.x.min +
                        point.index.x * this.nativeIterationBounds.x.step;
                point.position.y =
                    this.nativeIterationBounds.y.max +
                        point.index.y * this.nativeIterationBounds.y.step;
                return point;
            }
            else {
                return undefined;
            }
        };
    }
    // public getAsMessage(): any {
    // 	return {
    // 		key: this.getKey(),
    // 		projecttion: this.projection(),
    // 		nativeIterationBounds: this.getNativeIterationBounds(),
    // 		timeRange: this.timeRange(),
    // 		nativeStapSize: this.getNativeStepSize(),
    // 		outsideBoundsValue: this.getOutsideBoundsValue(),
    // 		title: this.getTitle(),
    // 		unit: this.getUnit(),
    // 		properties: this.getProperties(),
    // 		valuesAlreadyPrepared: true,
    // 		sources: this.getSources()
    // 	};
    // }
    getNativeIterationBounds() {
        return this.nativeIterationBounds;
    }
    getNativeStepSize() {
        return this.nativeStepSize;
    }
    getValues() {
        return this.values;
    }
    range() {
        return this.valueRange;
    }
    getOutsideBoundsValue() {
        return this.outsideBoundsValue;
    }
    value(x, y) {
        let p11 = this.point(x, y);
        if (p11) {
            //check boundaries
            let atXEnd = p11.t.x === 0 && p11.index.x == this.dimensions.x - 1;
            let atYEnd = p11.t.y === 0 && p11.index.y == this.dimensions.y - 1;
            if (atXEnd || atYEnd) {
                p11.index.x -= atXEnd ? 1 : 0;
                p11.index.y -= atYEnd ? 1 : 0;
                p11.value = this.nativeValue(p11.index.x, p11.index.y);
                p11.t.x = atXEnd ? 1.0 : p11.t.x;
                p11.t.y = atYEnd ? 1.0 : p11.t.y;
            }
            let p = [
                [
                    this.nativeValue(p11.index.x - 1, p11.index.y - 1),
                    this.nativeValue(p11.index.x - 1, p11.index.y - 0),
                    this.nativeValue(p11.index.x - 1, p11.index.y + 1),
                    this.nativeValue(p11.index.x - 1, p11.index.y + 2),
                ],
                [
                    this.nativeValue(p11.index.x - 0, p11.index.y - 1),
                    p11.value,
                    this.nativeValue(p11.index.x - 0, p11.index.y + 1),
                    this.nativeValue(p11.index.x - 0, p11.index.y + 2),
                ],
                [
                    this.nativeValue(p11.index.x + 1, p11.index.y - 1),
                    this.nativeValue(p11.index.x + 1, p11.index.y - 0),
                    this.nativeValue(p11.index.x + 1, p11.index.y + 1),
                    this.nativeValue(p11.index.x + 1, p11.index.y + 2),
                ],
                [
                    this.nativeValue(p11.index.x + 2, p11.index.y - 1),
                    this.nativeValue(p11.index.x + 2, p11.index.y - 0),
                    this.nativeValue(p11.index.x + 2, p11.index.y + 1),
                    this.nativeValue(p11.index.x + 2, p11.index.y + 2),
                ],
            ];
            return CubicInterpolator.biValue(p, p11.t.x, p11.t.y);
        }
        else {
            //outside bounds
            return this.outsideBoundsValue;
        }
    }
    /*
          static initializeIteration( start:Point, dimensions:Point, step:Point): IterationBounds {
              //we need to get our bearings straight. What is up what is down? What is left what is right?
              let internalIteration :IterationBounds ={
                      x: { min: undefined, max: undefined, step: undefined, count: dimensions.x },
                      y: { min: undefined, max: undefined, step: undefined, count: dimensions.y }
              }
          
              internalIteration.x.step = Math.abs(step.x);//always positive
              internalIteration.y.step = -1 * Math.abs(step.y);//always negative
              internalIteration.x.min = step.x > 0 ? start.x : start.x + (dimensions.x-1) * (step.x);
              internalIteration.y.max = step.y < 0 ? start.y : start.y + (dimensions.y-1) * (step.y);
              internalIteration.x.max = internalIteration.x.min + (dimensions.x - 1) * internalIteration.x.step;
              internalIteration.y.min = internalIteration.y.max + (dimensions.y - 1) * internalIteration.y.step;
              return internalIteration;
          }
        */
    //add a ring of interpolated values around the originals
    prepareValues() {
        const nativeIteration = this.utmBounds();
        const dimensions = {
            x: nativeIteration.x.count,
            y: nativeIteration.y.count,
        };
        //define this helper function
        const value = (x, y) => this.nativeIterationBounds.rowOrientation
            ? this.values[x + y * this.dimensions.x]
            : this.values[y + x * this.dimensions.y];
        const virtualValue = (x, y) => this.nativeIterationBounds.rowOrientation
            ? this.values[x + y * (this.dimensions.x + 2)]
            : this.values[y + x * (this.dimensions.y + 2)];
        const virtualP = (p0, p1) => 2 * p0 - p1;
        let i, p0, p1;
        //first calculate the new virtual corner points which are a little special case
        const p_00 = virtualP(value(0, 0), value(1, 1));
        const p_n0 = virtualP(value(this.dimensions.x - 1, 0), value(this.dimensions.x - 2, 1));
        const p_0n = virtualP(value(0, this.dimensions.y - 1), value(1, this.dimensions.y - 2));
        const p_nn = virtualP(value(this.dimensions.x - 1, this.dimensions.y - 1), value(this.dimensions.x - 2, this.dimensions.y - 2));
        if (this.nativeIterationBounds.rowOrientation) {
            //give each row a new start and end
            for (i = this.values.length; i > 0;) {
                //add new (virtual) end point
                p0 = this.values[i - 2];
                p1 = this.values[i - 1];
                this.values.splice(i, 0, virtualP(p1, p0));
                //add new (virtual) start point
                i -= this.dimensions.x;
                p0 = this.values[i];
                p1 = this.values[i + 1];
                this.values.splice(i, 0, virtualP(p0, p1));
            }
            //create new first row
            const first = new Array(this.dimensions.x + 2)
                .fill(0)
                .map((slot, i) => virtualP(virtualValue(i, 0), virtualValue(i, 1)));
            //set the special cases
            first.splice(0, 1, p_00);
            first.splice(first.length - 1, 1, p_n0);
            //create new last row
            const last = new Array(this.dimensions.x + 2)
                .fill(0)
                .map((slot, i) => virtualP(virtualValue(i, this.dimensions.y - 1), virtualValue(i, this.dimensions.y - 2)));
            //set the special cases
            last.splice(0, 1, p_0n);
            last.splice(last.length - 1, 1, p_nn);
            //add first and last to values
            this.values.splice(0, 0, ...first);
            this.values.splice(this.values.length, 0, ...last);
        }
        else {
            //give each column a new start and end
            for (i = this.values.length; i > 0;) {
                //add new (virtual) end point
                p0 = this.values[i - 2];
                p1 = this.values[i - 1];
                this.values.splice(i, 0, virtualP(p1, p0));
                //add new (virtual) start point
                i -= this.dimensions.y;
                p0 = this.values[i];
                p1 = this.values[i + 1];
                this.values.splice(i, 0, virtualP(p0, p1));
            }
            //create new first column
            const first = new Array(this.dimensions.y + 2)
                .fill(0)
                .map((slot, i) => virtualP(virtualValue(0, i), virtualValue(1, i)));
            //set the special cases
            first.splice(0, 1, p_00);
            first.splice(first.length - 1, 1, p_0n);
            //create new last column
            const last = new Array(this.dimensions.y + 2)
                .fill(0)
                .map((slot, i) => virtualP(virtualValue(this.dimensions.x - 1, i), virtualValue(this.dimensions.x - 2, i)));
            //set the special cases
            last.splice(0, 1, p_n0);
            last.splice(last.length - 1, 1, p_nn);
            //add first and last to values
            this.values.splice(0, 0, ...first);
            this.values.splice(this.values.length, 0, ...last);
        }
    }
}
export class BufferedValueMappedScalarField extends BufferedScalarField {
    //	constructor(sourceScalarField: BufferedScalarField, sources: Set<string>[], valueMapper: (value: number, index: number, values: number[]) => number) {
    constructor(sourceScalarField, valueMapper) {
        super(sourceScalarField.getKey(), sourceScalarField.projection(), sourceScalarField.getNativeIterationBounds(), sourceScalarField.timeRange(), sourceScalarField.getNativeStepSize(), sourceScalarField.getValues().map(valueMapper), sourceScalarField.getOutsideBoundsValue(), sourceScalarField.getTitle(), sourceScalarField.getUnit(), sourceScalarField.getProperties(), true);
        // this.addSources(sourceScalarField.getSources());
        // sources.forEach(sfSources => this.addSources(sfSources));
    }
}
export class ReducedScalarField extends BaseScalarField {
    scalarFields;
    reducer;
    constructor(scalarFields, reducer) {
        super(undefined, scalarFields[0].projection(), scalarFields.reduce((bounds, scalarField) => {
            const sfBounds = scalarField.utmBounds();
            if (bounds === undefined) {
                return {
                    x: { min: sfBounds.x.min, max: sfBounds.x.max },
                    y: { min: sfBounds.y.min, max: sfBounds.y.max }
                };
            }
            else {
                bounds.x.min = Math.min(sfBounds.x.min, bounds.x.min);
                bounds.x.max = Math.max(sfBounds.x.max, bounds.x.max);
                bounds.y.min = Math.min(sfBounds.y.min, bounds.y.min);
                bounds.y.max = Math.max(sfBounds.y.max, bounds.y.max);
                return bounds;
            }
        }, undefined), {
            start: scalarFields[0].timeRange().start,
            end: scalarFields[scalarFields.length - 1].timeRange().end,
            //width: scalarFields[scalarFields.length - 1].timeRange().end - scalarFields[0].timeRange().start
        }, scalarFields[0].getStepSize());
        this.scalarFields = scalarFields;
        this.reducer = reducer;
        //scalarFields.forEach(sf => this.addSources(sf.getSources()));
    }
    value(x, y) {
        return this.scalarFields
            .map(scalarSource => scalarSource.value(x, y))
            .reduce(this.reducer, undefined);
    }
}
export class ScalarFieldSequenceImpl {
    fadeToValue;
    fadeMillis;
    scalarFields;
    lookupList;
    constructor(fadeToValue = 0.0, fadeMillis = 600000) {
        this.fadeToValue = fadeToValue;
        this.fadeMillis = fadeMillis;
        this.scalarFields = [];
        this.lookupList = [];
    }
    isEmpty() {
        return this.scalarFields ? this.scalarFields.length === 0 : true;
    }
    clear() {
        this.scalarFields = [];
        this.lookupList = [];
        return this;
    }
    //   public removeScalarField(cacheKey: CacheKey): ScalarField | undefined {
    //     const index = this.scalarFields.findIndex(
    //       (sf) => sf.getCacheKey() && sf.getCacheKey().key === cacheKey.key
    //     );
    //     if (index !== -1) {
    //       const sf = this.scalarFields.splice(index, 1)[0];
    //       this.updateScalarFieldLookup();
    //       return sf;
    //     }
    //     return undefined;
    //   }
    addScalarField(source) {
        this.scalarFields.push(source);
        this.updateScalarFieldLookup();
        return this;
    }
    setScalarFields(scalarFields) {
        this.scalarFields = scalarFields;
        this.updateScalarFieldLookup();
        return this;
    }
    getScalarFields() {
        return this.scalarFields;
    }
    timeRange() {
        if (this.scalarFields && this.scalarFields.length > 0) {
            return {
                start: this.scalarFields[0].timeRange().start,
                end: this.scalarFields[this.scalarFields.length - 1].timeRange().end,
                // width:
                //   this.scalarFields[this.scalarFields.length - 1].timeRange().end -
                //   this.scalarFields[0].timeRange().start,
            };
        }
        else {
            return undefined;
        }
    }
    range() {
        if (this.scalarFields && this.scalarFields.length > 0) {
            return this.scalarFields.reduce((range, scalarField) => {
                if (scalarField.range) {
                    const valueRange = (scalarField).range();
                    if (valueRange) {
                        if (!range) {
                            range = valueRange;
                        }
                        else {
                            range.min = Math.min(range.min, valueRange.min);
                            range.max = Math.max(range.max, valueRange.max);
                        }
                    }
                }
                return range;
            }, undefined);
        }
        else {
            return undefined;
        }
    }
    getScalarField(time, reducer) {
        if (time !== undefined && this.lookupList && this.lookupList.length > 0) {
            if (!time.start) {
                //an instant in time is requested
                const millis = time;
                let i = 0;
                let scalarFieldInstant = undefined;
                while (i < this.lookupList.length && !scalarFieldInstant) {
                    if (this.lookupList[i].end <= millis) {
                        i++;
                    }
                    else {
                        if (this.lookupList[i].start <= millis) {
                            scalarFieldInstant = this.lookupList[i];
                            scalarFieldInstant.t =
                                (millis - scalarFieldInstant.start) /
                                    (scalarFieldInstant.end - scalarFieldInstant.start);
                        }
                        else {
                            i = this.lookupList.length;
                        }
                    }
                }
                if (scalarFieldInstant) {
                    let t = (millis - scalarFieldInstant.start) /
                        (scalarFieldInstant.end - scalarFieldInstant.start);
                    t = t < 0.0 ? 0.0 : t > 1.0 ? 1.0 : t;
                    let tInv = 1.0 - t;
                    //                public value( x: number, y: number ): number {
                    //                    return ( 1 - this.t) * this.sf0.value( x, y ) + this.t * this.sf1.value( x, y );
                    //                }
                    let sf0 = scalarFieldInstant.startSource !== undefined
                        ? this.scalarFields[scalarFieldInstant.startSource]
                        : undefined;
                    let sf1 = scalarFieldInstant.endSource !== undefined
                        ? this.scalarFields[scalarFieldInstant.endSource]
                        : undefined;
                    if (sf0 === undefined) {
                        const valueMapper = closeNumber(t, 0.0)
                            ? (_sourceValue, _valueIndex, _sourceValues) => this.fadeToValue
                            : closeNumber(t, 1.0)
                                ? (sourceValue, _valueIndex, _sourceValues) => sourceValue
                                : (sourceValue, _valueIndex, _sourceValues) => tInv * this.fadeToValue + t * sourceValue;
                        return new BufferedValueMappedScalarField(sf1, 
                        //              [],
                        valueMapper);
                        //sf0 = this.buildFadeField( sf1, scalarFieldInstant.start );
                    }
                    else if (sf1 === undefined) {
                        const valueMapper = closeNumber(t, 0.0)
                            ? (sourceValue, _valueIndex, _sourceValues) => sourceValue
                            : closeNumber(t, 1.0)
                                ? (_sourceValue, _valueIndex, _sourceValues) => this.fadeToValue
                                : (sourceValue, _valueIndex, _sourceValues) => tInv * sourceValue + t * this.fadeToValue;
                        return new BufferedValueMappedScalarField(sf0, 
                        //  [],
                        valueMapper);
                        //sf1 = this.buildFadeField( sf0, scalarFieldInstant.end );
                    }
                    else {
                        //both are defined
                        const valueMapper = closeNumber(t, 0.0)
                            ? (sourceValue, _valueIndex, _sourceValues) => sourceValue
                            : closeNumber(t, 1.0)
                                ? (_sourceValue, valueIndex, _sourceValues) => sf1.getValues()[valueIndex]
                                : (sourceValue, valueIndex, _sourceValues) => tInv * sourceValue +
                                    t * sf1.getValues()[valueIndex];
                        return new BufferedValueMappedScalarField(sf0, 
                        //              [sf1.getSources()],
                        valueMapper);
                    }
                }
            }
            else {
                //a time range is requested
                const timeRange = time;
                let scalarFields = this.scalarFields.filter((scalarField) => {
                    return (scalarField.timeRange().end >= timeRange.start &&
                        scalarField.timeRange().start <= timeRange.end);
                });
                if (scalarFields.length > 0) {
                    //default reducer for a sequence of scalar fields is 'max'. I.e. a footprint
                    return new ReducedScalarField(scalarFields, reducer ? reducer : ScalarFieldValueReducers.max);
                }
            }
        }
        return undefined;
    }
    updateScalarFieldLookup() {
        this.lookupList = [];
        if (this.scalarFields != null && this.scalarFields.length > 0) {
            //first sort the sources on their start millis
            this.scalarFields.sort((a, b) => a.timeRange().start - b.timeRange().start);
            let preliminaryLookUp = this.scalarFields.map((sf, index, list) => {
                const isLastSource = list.length - 1 === index;
                let nextSource = !isLastSource ? list[index + 1] : undefined;
                let endSource = nextSource && nextSource.timeRange().start === sf.timeRange().end
                    ? index + 1
                    : index;
                let sourceLookup = {
                    start: sf.timeRange().start,
                    end: sf.timeRange().end,
                    startSource: index,
                    endSource: endSource,
                };
                return sourceLookup;
            });
            this.lookupList = preliminaryLookUp.reduce((accu, lookup, _index, _preliminaryList) => {
                if (!accu.previous) {
                    //the very first source so we need to fade-in first
                    accu.lookupList.push({
                        start: lookup.start - this.fadeMillis,
                        end: lookup.start,
                        startSource: undefined,
                        endSource: lookup.startSource,
                    });
                }
                else if (accu.previous.endSource === undefined) {
                    //previous source is a fade-out transition so we need to fade in again.
                    let actualFadeMillis = (lookup.start - accu.previous.start) / 2;
                    let fadeInStart = lookup.start - this.fadeMillis;
                    if (actualFadeMillis < this.fadeMillis) {
                        //the available transition time is less then required for a full fade-out-in so we correct for a partial fade
                        accu.previous.end = accu.previous.start + actualFadeMillis;
                        fadeInStart = accu.previous.end;
                    }
                    else {
                        actualFadeMillis = this.fadeMillis;
                    }
                    //add the fade-in transition
                    accu.lookupList.push({
                        start: fadeInStart,
                        end: lookup.start,
                        startSource: undefined,
                        endSource: lookup.startSource,
                    });
                }
                //add the current sourceLookup to the list of look-ups
                accu.lookupList.push(lookup);
                accu.previous = lookup;
                //check if we need a fade-out
                if (lookup.startSource === lookup.endSource) {
                    //the startSource and endSource are equal so a fade-out is needed
                    accu.lookupList.push({
                        start: lookup.end,
                        end: lookup.end + this.fadeMillis,
                        startSource: lookup.endSource,
                        endSource: undefined,
                    });
                    accu.previous = accu.lookupList[accu.lookupList.length - 1];
                }
                return accu;
            }, { lookupList: [], previous: undefined }).lookupList;
        }
    }
}
